import { useTranslation } from "react-i18next"

import { Card, CardContent } from "@/components/ui/card"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { cn } from "@/lib/utils"

/**
 * OKS-63 — Single KPI tile on the Mission Analytics surfaces.
 *
 * Distinct from `DashboardKpiTile` (OKS-38): analytics tiles render
 * percent rates with **one decimal place** (PRD AC §5) and expose a
 * **hover Tooltip** showing the raw numerator / denominator (PRD AC §5
 * + UX Spec §2). The OKS-38 tile is whole-number-only + no tooltip.
 *
 * Two render modes via `kind`:
 *
 *   - `"integer"`: locale-aware thousands separator via
 *     `Intl.NumberFormat`. `0` rendered explicitly. No tooltip.
 *   - `"percent"`: `[0,1]` Float → "%XX.X" (1 decimal). `null` renders
 *     the em-dash with `emptyAriaKey` announcement. Tooltip surfaces
 *     "{num} of {denom} collabs" via `tooltipKey` interpolation; also
 *     mirrored in an `sr-only` span so screen readers get the num/den
 *     without focus + hover.
 *
 * A11y posture mirrors `DashboardKpiTile`: `role="group"` + an
 * `aria-label` summary on the card. The sr-only num/den span is the
 * accessible-name-secondary mechanism for the rate tiles.
 */

export type AnalyticsKpiTileKind = "integer" | "percent"

export interface AnalyticsKpiTileProps {
  /** i18n key resolving to the tile label (e.g. `analytics.kpis.acceptance_rate.label`). */
  labelKey: string
  /** i18n key resolving to the aria-label template (interpolates `{value}`). */
  ariaLabelKey: string
  /**
   * i18n key for the empty/null aria-label. Required when value can be
   * null (`percent` tiles). When `value === null` and this key is set,
   * it replaces `ariaLabelKey`.
   */
  emptyAriaKey?: string
  /**
   * Numeric value. `null` is only legitimate for `kind: "percent"`
   * (when denominator is zero) — renders the em-dash.
   */
  value: number | null
  kind?: AnalyticsKpiTileKind
  /**
   * Raw numerator + denominator for the tooltip + sr-only num/den
   * announcement. Required when `kind === "percent"` AND `tooltipKey`
   * is set. Falsey value-pairs render no tooltip.
   */
  numerator?: number
  denominator?: number
  /**
   * i18n key for the tooltip body, interpolates `{num}` + `{denom}`.
   * When omitted, no tooltip is rendered (use for the non-rate tiles).
   */
  tooltipKey?: string
  /** Stable test hook for system + Vitest specs. */
  testId?: string
  className?: string
}

const EM_DASH = "—"

export function AnalyticsKpiTile({
  labelKey,
  ariaLabelKey,
  emptyAriaKey,
  value,
  kind = "integer",
  numerator,
  denominator,
  tooltipKey,
  testId,
  className,
}: AnalyticsKpiTileProps) {
  const { t, i18n } = useTranslation()

  const isNull = value === null
  const displayValue = formatValue(value, kind, i18n.language)
  const ariaLabel =
    isNull && emptyAriaKey
      ? t(emptyAriaKey)
      : t(ariaLabelKey, { value: displayValue })

  const showTooltip =
    tooltipKey != null && numerator != null && denominator != null

  const inner = (
    <Card
      data-testid={testId}
      role="group"
      aria-label={ariaLabel}
      className={cn("rounded-xl border p-5", className)}
    >
      <CardContent className="flex flex-col gap-1 p-0">
        <span className="text-muted-foreground text-sm font-medium">
          {t(labelKey)}
        </span>
        <span
          className="text-foreground text-3xl font-semibold tracking-tight"
          // Decorative — the announcement comes from the card's aria-label.
          aria-hidden={isNull ? true : undefined}
        >
          {displayValue}
        </span>
        {/* sr-only num/den fallback — screen readers announce the raw
            ratio without the user having to hover the tooltip. */}
        {showTooltip ? (
          <span className="sr-only" data-testid={`${testId}-sr-numden`}>
            {t(tooltipKey, { num: numerator, denom: denominator })}
          </span>
        ) : null}
      </CardContent>
    </Card>
  )

  if (!showTooltip) return inner

  return (
    <TooltipProvider delayDuration={120}>
      <Tooltip>
        <TooltipTrigger render={inner} />
        <TooltipContent side="bottom">
          {t(tooltipKey, { num: numerator, denom: denominator })}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

function formatValue(
  value: number | null,
  kind: AnalyticsKpiTileKind,
  locale: string,
): string {
  if (value === null) return EM_DASH

  if (kind === "percent") {
    // [0,1] ratio → 1-decimal-place percent (PRD AC §5).
    return new Intl.NumberFormat(locale, {
      style: "percent",
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    }).format(value)
  }

  return new Intl.NumberFormat(locale).format(value)
}
